import { CircleNotch } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  Flex,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { campaignRoute } from "../routes/ask-puntt/campaignRoute";
import { useAnalytics } from "../utils/analytics";
import { useCreateAskPunttProject } from "../utils/queries/askPunttProjects";

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Self-contained "create a new project" dialog. Mirrors the create flow on
 * the Campaigns launcher (`routes/ask-puntt/campaigns.tsx`) but is portable so
 * it can be triggered from anywhere — currently the sidebar "Projects +"
 * affordance. On a successful create it navigates the user straight into the
 * new project workspace, matching the launcher's behaviour.
 */
export function CreateProjectDialog({
  open,
  onOpenChange,
}: CreateProjectDialogProps) {
  const navigate = useNavigate();
  const analytics = useAnalytics("create_project_dialog");
  const createProject = useCreateAskPunttProject();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setName("");
      setDescription("");
    }
    onOpenChange(next);
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await createProject.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
      });
      analytics.capture("ask_puntt_campaign_created", {
        projectId: result.project._id,
        source: "sidebar",
      });
      setName("");
      setDescription("");
      onOpenChange(false);
      navigate({
        to: campaignRoute.to,
        params: { projectId: result.project._id },
      });
    } catch (error) {
      // The mutation hook surfaces a toast on error; keep the dialog open so
      // the user can retry without re-typing.
      console.error("Failed to create project:", error);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        maxWidth="460px"
        data-testid="create-project-dialog"
        aria-label="Create a project"
      >
        <Dialog.Title>New project</Dialog.Title>
        <Dialog.Description size="2" className="text-puntt-neutral-gray-11">
          Puntt creates a shared chat for the project automatically. You can add
          personal chats and documents once it&apos;s open.
        </Dialog.Description>
        <label htmlFor="create-project-name" className="mt-3 block">
          <Text size="1" weight="medium" className="text-puntt-neutral-gray-11">
            Name
          </Text>
          <TextField.Root
            id="create-project-name"
            data-testid="create-project-name"
            className="mt-1"
            placeholder="e.g. NESCAFÉ Ice Roast — Spring '26 Launch"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <label htmlFor="create-project-description" className="mt-3 block">
          <Text size="1" weight="medium" className="text-puntt-neutral-gray-11">
            Description (optional)
          </Text>
          <TextArea
            id="create-project-description"
            data-testid="create-project-description"
            className="mt-1"
            placeholder="What is this project about?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ height: 72 }}
          />
        </label>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              data-testid="create-project-cancel"
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            data-testid="create-project-submit"
            disabled={!name.trim() || createProject.isPending}
            onClick={handleCreate}
          >
            {createProject.isPending ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : null}
            Create project
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
